
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Layout } from '@/components/layout/Layout';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { getPostById, getCommentsByPostId } from '@/services/postService';
import { useQuery } from '@tanstack/react-query';
import { PostDetailHeader } from '@/components/post/PostDetailHeader';
import { CommentsSection } from '@/components/post/CommentsSection';

const PostDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [liked, setLiked] = useState(false);
  
  const { 
    data: post, 
    isLoading: isLoadingPost, 
    error: postError,
    refetch: refetchPost 
  } = useQuery({
    queryKey: ['post', id],
    queryFn: () => getPostById(id!),
    enabled: !!id,
    staleTime: 0, // Disable caching to ensure we get fresh data with updated view count
  });
  
  const { 
    data: comments = [], 
    isLoading: isLoadingComments,
    error: commentsError
  } = useQuery({
    queryKey: ['comments', id],
    queryFn: () => getCommentsByPostId(id!),
    enabled: !!id,
  });
  
  useEffect(() => {
    if (postError) {
      toast({
        variant: "destructive",
        title: "게시글을 불러오는데 실패했습니다",
        description: "잠시 후 다시 시도해주세요."
      });
      navigate('/board');
    }
    if (commentsError) {
      toast({
        variant: "destructive",
        title: "댓글을 불러오는데 실패했습니다",
        description: "잠시 후 다시 시도해주세요."
      });
    }
  }, [postError, commentsError, toast, navigate]);
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoadingPost) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4 flex items-center justify-center">
          <p>게시글을 불러오는 중...</p>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4 flex flex-col items-center justify-center">
          <h2 className="text-2xl font-bold mb-4">게시글을 찾을 수 없습니다</h2>
          <Button asChild>
            <Link to="/board">게시판으로 돌아가기</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <PostDetailHeader 
            post={post}
            liked={liked}
            setLiked={setLiked}
            formatDate={formatDate}
          />
          
          <CommentsSection
            postId={id!}
            comments={comments}
            isLoading={isLoadingComments}
            user={user}
            formatDate={formatDate}
          />
        </div>
      </div>
    </Layout>
  );
};

export default PostDetail;
